import React, { useState } from 'react';
import api from '../services/api';
import { X, Send, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';

const SendMessageModal = ({ isOpen, onClose, student, onSent }) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSend = async () => {
        if (!message.trim()) {
            setError('Please enter a message');
            return;
        }

        if (!student || !student.id) {
            setError('Recipient information is missing');
            return;
        }

        try {
            setSending(true);
            setError('');

            const endpoint = '/admin/messages/send';

            await api.post(endpoint, {
                recipient_id: student.id,
                content: message.trim()
            });

            setSuccess(true);
            setTimeout(() => {
                setMessage('');
                setSuccess(false);
                if (onSent) {
                    onSent();
                }
                onClose();
            }, 1500);
        } catch (err) {
            console.error('Error sending message:', err);
            setError(err.response?.data?.error || 'Failed to send message. Please try again.');
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center text-white">
                    <div className="flex items-center space-x-3">
                        <MessageSquare className="h-6 w-6" />
                        <h2 className="text-xl font-bold">Send Message</h2>
                    </div>
                    <button onClick={onClose} className="hover:text-blue-100 transition-colors">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6">
                    {success ? (
                        <div className="text-center py-8">
                            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                            <p className="text-gray-600">The message has been sent to {student?.name || 'the candidate'}.</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Recipient</p>
                                <div className="flex items-center space-x-3 bg-gray-50 p-3 rounded-xl border border-gray-100">
                                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                        {(student?.name || 'C').charAt(0)}
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-900">{student?.name || 'Unknown Candidate'}</div>
                                        <div className="text-xs text-gray-500">{student?.email || 'No email provided'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Your Message</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => {
                                        setMessage(e.target.value);
                                        setError('');
                                    }}
                                    placeholder="Type your message here..."
                                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none min-h-[160px] ${error ? 'border-red-300 bg-red-50' : 'border-gray-100'
                                        }`}
                                />
                                {error && <p className="mt-2 text-sm text-red-600 font-medium flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" /> {error}
                                </p>}
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-all text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSend}
                                    disabled={sending || !message.trim()}
                                    className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {sending ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4" />
                                            Send Message
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SendMessageModal;
